import classNames from 'classnames';
import { AsYouTypeFormatter } from 'google-libphonenumber';
import React, { Fragment } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import { makeStyles } from '@mui/styles';

import { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import { BaseEnum, MemberPhone } from '@vestahealthcare/common/models';

import { IconButton, Select, TextInput } from 'styleguide-v2';

import { MemberPhoneUpdateParam } from 'dash/src/services/PatientServices';

const useStyles = makeStyles({
  container: {
    height: 'fit-content',
  },
  firstRadio: {
    height: '7rem',
  },
  radio: {
    '&& .MuiFormGroup-root': {
      margin: '1.5rem 0 0 1.5rem',
    },
  },
  firstIcon: {
    '&&': {
      margin: '2.25rem auto auto',
    },
  },
  icon: {
    alignSelf: 'baseline',
    margin: '0.5rem auto auto',
  },
});

const normalize = (str?: string) => str?.replace(/\D/g, '') ?? '';
const formatter = new AsYouTypeFormatter('US');
const formatAsPhoneNumber = (str?: string): string => {
  formatter.clear();
  return [...normalize(str)]
    .slice(0, 10)
    .reduce((_total, digit) => formatter.inputDigit(digit), '');
};

type Props = {
  className?: string;
  disabled?: boolean;
  phones: MemberPhoneUpdateParam[];
  phoneLabel: string;
  reasonLabel: string;
  reasons: Selectable[];
  submitted?: boolean;
  onChange: (
    phones: MemberPhoneUpdateParam[],
    deletedPhone?: MemberPhone,
  ) => void;
};

export const DeletePhoneGroup = ({
  className,
  disabled,
  phones,
  phoneLabel,
  reasonLabel,
  reasons,
  submitted,
  onChange,
}: Props) => {
  const styles = useStyles();

  const handleChange = (
    phones?: MemberPhoneUpdateParam[],
    deletedPhone?: MemberPhone,
  ) => onChange([...(phones || [])] as MemberPhone[], deletedPhone);

  return (
    <div
      className={classNames(className, styles.container, 'grid-wrapper fit')}
    >
      {[...((phones || []) as MemberPhoneUpdateParam[])].map((phone, idx) => (
        <Fragment key={`delete-phone-${phone.id}-${idx}`}>
          <TextInput
            className="grid-span-5"
            disabled
            label={
              idx === 0
                ? phoneLabel || translate('components.editPhones.phone')
                : undefined
            }
            onChange={() => {}}
            value={
              phone.phone?.number
                ? formatAsPhoneNumber(phone.phone?.number)
                : undefined
            }
          />
          <Select
            className="grid-span-6"
            disabled={disabled}
            disableClearable
            error={submitted && !phone.reason}
            items={reasons}
            label={idx === 0 ? reasonLabel : undefined}
            onChange={(reason: Selectable) => {
              phone.reason = reason;
              handleChange(phones);
            }}
            value={phone.reason}
            required
          />
          <IconButton
            className={classNames(
              idx === 0 && styles.firstIcon,
              styles.icon,
              'grid-span-1',
            )}
            disabled={disabled}
            size="small"
            onClick={() => {
              phones?.splice(idx, 1);
              delete phone.reason;
              delete phone.reasonOther;
              handleChange(
                phones,
                phone?.id ? (phone as MemberPhone) : undefined,
              );
            }}
            tooltip={phone.inactivatedAt ? translate('global.restore') : ''}
          >
            {phone.inactivatedAt ? (
              <RestoreOutlinedIcon fontSize="large" />
            ) : (
              <CloseIcon fontSize="large" />
            )}
          </IconButton>
          {phone.reason?.value === BaseEnum.OTHER && (
            <TextInput
              className="grid-span-11"
              disabled={disabled}
              error={submitted && !phone.reasonOther}
              placeholder={translate('global.other')}
              onChange={(reasonOther?: string) => {
                phone.reasonOther = reasonOther;
                handleChange(phones);
              }}
              value={phone.reasonOther}
              required
            />
          )}
        </Fragment>
      ))}
    </div>
  );
};
